import {
  Ids,
  Image,
  Images,
  SignedUrlRelation,
  SignedUrlsResponse,
} from "../types/images";
import { httpClient, HttpError } from "@coworker/http-client";
import { FileUploadType } from "@fixa/file-upload";
import {
  NO_WRITE_SIGNED_URLS,
  SignedUrlResponse,
} from "@coworker/apprestructured/src/task-management/services/uploads.service";

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const getChunkSignedUrls = async (
  fileNameChunk: string[],
  chunkNumber: number,
  size: "small" | "large",
  maxRetries: number
): Promise<string[]> => {
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("filenames", fileNameChunk.join(","));
  urlSearchParams.append("size", size);

  let chunkData: SignedUrlsResponse | null = null;
  let chunkError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (attempt > 0) {
      await wait(1000 * Math.pow(2, attempt - 1));
    }

    try {
      chunkData = await httpClient.roomsettings.get<SignedUrlsResponse>(
        `/images/get_signed_urls?${urlSearchParams.toString()}`
      );
      chunkError = null;
      break;
    } catch (error) {
      chunkError = error instanceof Error ? error : new Error(String(error));
    }
  }

  if (chunkData && Array.isArray(chunkData.urls)) {
    return chunkData.urls;
  }

  if (chunkError) {
    console.error(
      `Fetch error while getting signed URLs for chunk ${chunkNumber}:`,
      chunkError
    );
  }

  return [];
};

export const getImagesByMediaId = async (mediaId: string, groupId?: string) => {
  if (!mediaId) return;
  try {
    const groupIdParam = groupId
      ? `?groupId=${encodeURIComponent(groupId)}`
      : "";
    const data = await httpClient.roomsettings.get<{ images: Images }>(
      `/images/medias/${mediaId}${groupIdParam}`
    );
    return data.images;
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) return [];
    console.error("Fetch error:", error);
  }
  return null;
};

export const getSignedUrls = async (
  fileNames: string[],
  size: "small" | "large"
): Promise<SignedUrlRelation[]> => {
  const CHUNK_SIZE = 50;
  const MAX_RETRIES = 4;

  if (fileNames.length === 0) {
    return [];
  }

  try {
    const chunkPromises: Promise<string[]>[] = [];

    for (let i = 0; i < fileNames.length; i += CHUNK_SIZE) {
      const fileNameChunk = fileNames.slice(i, i + CHUNK_SIZE);
      chunkPromises.push(
        getChunkSignedUrls(fileNameChunk, i / CHUNK_SIZE + 1, size, MAX_RETRIES)
      );
    }

    const signedUrls = (await Promise.all(chunkPromises)).flat();

    return fileNames.map((fileName) => {
      const signedUrl =
        signedUrls.find(
          (url) =>
            url.includes(fileName) || url.includes(encodeURIComponent(fileName))
        ) ?? "";

      return {
        fileName,
        signedUrl,
      };
    });
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return [];
};

export async function getWriteSignedUrls(
  fileNames: string[],
  uploadType: FileUploadType
): Promise<SignedUrlResponse[]> {
  if (fileNames.length === 0) {
    return NO_WRITE_SIGNED_URLS;
  }
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("fileNames", JSON.stringify(fileNames));
  urlSearchParams.append("uploadType", uploadType);
  return httpClient.roomsettings.get<SignedUrlResponse[]>(
    `/uploads/write-signed-urls?${urlSearchParams.toString()}`
  );
}

export const addImageToMedia = async (image: Image) => {
  return httpClient.roomsettings.post<Ids>(
    `/images/medias/${image.mediaId}/image-upload`,
    { body: [image] }
  );
};

export const deleteMediaImages = async (imageIds: string) => {
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("imageIds", imageIds);
  await httpClient.roomsettings.delete(`/images?${urlSearchParams.toString()}`);
};

export const updateImages = async (mediaId: string, images: Image[]) => {
  const CHUNK_SIZE = 200;
  const MAX_RETRIES = 3;

  for (let i = 0; i < images.length; i += CHUNK_SIZE) {
    const imageChunk = images.slice(i, i + CHUNK_SIZE);
    const body = imageChunk
      .filter((image) => image.id)
      .map(({ id, rotation, orderIndex }) => ({
        id,
        rotation,
        orderIndex,
      }));

    let lastError: Error | null = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, attempt - 1))
        );
      }

      try {
        await httpClient.roomsettings.patch(`/images/medias/${mediaId}`, {
          body,
        });
        lastError = null;
        break;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
      }
    }

    if (lastError) {
      throw lastError;
    }
  }
};
